import { authApi, isAuthError, type RegisterRequest } from '@/api/login';
import { ErrorBanner, FormField, PasswordFields, usePasswordValidation } from '@/components';
import { RegisterHeader } from '@/features/register/RegisterHeader';
import { Link } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';



export function RegisterForm({ onSuccess }: { onSuccess: (email: string) => void }) {
    const { t } = useTranslation();
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [emailTouched, setEmailTouched] = useState(false);
    const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const passwordValidation = usePasswordValidation(password, confirmPassword);

    // Email validation
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    // All requirements met for enabling the button
    const canSubmit = username.trim() && passwordValidation.allRequirementsMet && passwordValidation.passwordsMatch && isValidEmail;

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const registerData: RegisterRequest = {
                username: username.trim(),
                email: email.trim().toLowerCase(),
                password
            };

            await authApi.register(registerData);
            onSuccess(email.trim());
        } catch (err) {
            if (isAuthError(err)) {
                setError(err.message);
            } else {
                setError('Registration failed. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="w-full">

            {/* Header */}
            <RegisterHeader
                title={t('auth.register', 'Create account')}
                subtitle={t('auth.registerSubtitle', 'Fill in your details to get started')}
            />

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <FormField
                        label={t('auth.username', 'Username')}
                        type="text"
                        name="username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="johndoe"
                        required
                        autoComplete="username"
                    />
                </div>

                <div>
                    <FormField
                        label={t('auth.email', 'Email')}
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={() => setEmailTouched(true)}
                        placeholder="you@example.com"
                        required
                        autoComplete="email"
                    />
                    <div className={`mt-1 overflow-hidden transition-all duration-300 ${emailTouched && !isValidEmail ? 'max-h-10 opacity-100' : 'max-h-0 opacity-0'
                        }`}>
                        <p className="text-sm text-error flex items-center gap-1">
                            <X className="size-4" />
                            {t('auth.invalidEmail', 'Please enter a valid email address')}
                        </p>
                    </div>
                </div>

                <PasswordFields
                    password={password}
                    confirmPassword={confirmPassword}
                    onPasswordChange={setPassword}
                    onConfirmPasswordChange={setConfirmPassword}
                    confirmPasswordTouched={confirmPasswordTouched}
                    onConfirmPasswordBlur={() => setConfirmPasswordTouched(true)}
                />


                <ErrorBanner hasError={!!error}>
                    {error}
                </ErrorBanner>

                <button
                    type="submit"
                    disabled={isLoading || !canSubmit}
                    className="w-full py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-primary text-onprimary hover:bg-primary-darker focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                >
                    {isLoading ? (
                        <span className="animate-spin inline-block size-4 border-2 border-current border-t-transparent rounded-full" />
                    ) : null}
                    {t('auth.registerButton', 'Create account')}
                </button>
            </form>

            {/* Login link */}
            <div className="mt-6 text-center">
                <p className="text-sm">
                    <span className="opacity-50">
                        {t('auth.hasAccount', 'Already have an account?')}{' '}
                    </span>
                    <Link
                        to="/login"
                        className="text-primary hover:text-primary/80 font-medium transition-colors"
                    >
                        {t('auth.loginLink', 'Sign in')}
                    </Link>
                </p>
            </div>


        </div>
    );
}